// Voice announcements via Web Speech API (SpeechSynthesis).
// No external fetch, no API key — built into all modern browsers.
// Works offline. Great for kids who can't read yet.

let cachedVoices: SpeechSynthesisVoice[] = [];

const loadVoices = (): Promise<SpeechSynthesisVoice[]> => {
    return new Promise((resolve) => {
        if (typeof window === 'undefined' || !window.speechSynthesis) {
            resolve([]);
            return;
        }
        const existing = window.speechSynthesis.getVoices();
        if (existing.length > 0) {
            cachedVoices = existing;
            resolve(existing);
            return;
        }
        // Voices load asynchronously in some browsers
        const handler = () => {
            cachedVoices = window.speechSynthesis.getVoices();
            window.speechSynthesis.onvoiceschanged = null;
            resolve(cachedVoices);
        };
        window.speechSynthesis.onvoiceschanged = handler;
        // Fallback timeout
        setTimeout(() => resolve(window.speechSynthesis.getVoices()), 1000);
    });
};

// Warm up the voice cache on first user interaction
if (typeof window !== 'undefined' && window.speechSynthesis) {
    loadVoices();
}

export type SpeakOptions = {
    /** Rate: 0.1 (slow) to 10 (fast). Default 1. Kids prefer ~0.9. */
    rate?: number;
    /** Pitch: 0 (low) to 2 (high). Default 1. Kids prefer ~1.2 (cheerful). */
    pitch?: number;
    /** Volume: 0 to 1. Default 1. */
    volume?: number;
};

/**
 * Speak a phrase aloud. Falls back silently if speech synthesis is unavailable.
 */
export const speak = (text: string, opts: SpeakOptions = {}): void => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
        console.warn('Speech synthesis not supported');
        return;
    }

    // Cancel any in-progress speech to avoid queue buildup
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = opts.rate ?? 0.9;
    utterance.pitch = opts.pitch ?? 1.2;
    utterance.volume = opts.volume ?? 1;

    // Pick a friendly voice if available — prefer English voices
    const voices = cachedVoices.length > 0 ? cachedVoices : window.speechSynthesis.getVoices();
    if (voices.length > 0) {
        const preferred =
            voices.find((v) => v.lang.startsWith('en') && v.name.includes('Google')) ||
            voices.find((v) => v.lang.startsWith('en')) ||
            voices[0];
        if (preferred) utterance.voice = preferred;
    }

    window.speechSynthesis.speak(utterance);
};

/** Stop any current speech. */
export const stopSpeaking = (): void => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
    }
};

/** Returns true if the browser supports speech synthesis. */
export const isSpeechSupported = (): boolean =>
    typeof window !== 'undefined' && 'speechSynthesis' in window;
